import { useEffect, useState } from "react";
import { Link, NavLink, useLocation } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import { ArrowUpRight, Instagram, Menu, X } from "lucide-react";
import Marquee from "react-fast-marquee";
import { NAV_LINKS, DIAG_URL, EMAIL_URL, WPP_URL, INSTA_URL, LINKEDIN_URL } from "@/links";
import { updateAnalyticsConsent } from "@/analytics";

const Logo = ({ onClick }) => (
  <Link to="/" onClick={onClick} data-testid="logo-link" className="group flex items-end gap-1.5 select-none">
    <img
      src="/assets/logo-white.png"
      alt="FUCHS — Posicionamento e Direção de Marcas"
      className="h-6 w-auto transition-opacity duration-300 group-hover:opacity-80"
    />
    <span className="mb-0.5 h-2 w-2 rounded-full bg-gradient-to-br from-brand-orange to-brand-magenta transition-transform duration-300 group-hover:scale-150" />
  </Link>
);

export const LogoNavy = ({ className = "" }) => (
  <div className={`flex items-end gap-1.5 ${className}`} data-testid="logo-navy">
    <img src="/assets/logo-navy.png" alt="FUCHS — Posicionamento e Direção de Marcas" className="h-5 w-auto" />
    <span className="mb-0.5 h-1.5 w-1.5 rounded-full bg-gradient-to-br from-brand-orange to-brand-magenta" />
  </div>
);

const Header = () => {
  const [open, setOpen] = useState(false);
  const [scrolled, setScrolled] = useState(false);
  const location = useLocation();

  useEffect(() => setOpen(false), [location.pathname]);
  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 24);
    window.addEventListener("scroll", onScroll, { passive: true });
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  return (
    <>
      <header
        data-testid="site-header"
        className={`fixed inset-x-0 top-0 z-50 border-b transition-all duration-500 ${
          scrolled ? "border-white/10 bg-ink/85 backdrop-blur-xl" : "border-transparent bg-transparent"
        }`}
      >
        <div className="mx-auto flex h-20 max-w-7xl items-center justify-between px-6 lg:px-10">
          <Logo />
          <nav className="hidden items-center gap-9 lg:flex" data-testid="desktop-nav">
            {NAV_LINKS.map((l) => (
              <NavLink
                key={l.to}
                to={l.to}
                data-testid={l.testid}
                className={({ isActive }) =>
                  `relative font-mono text-[11px] uppercase tracking-[0.22em] transition-colors duration-300 ${
                    isActive ? "text-white" : "text-slate-400 hover:text-white"
                  }`
                }
              >
                {({ isActive }) => (
                  <>
                    {l.label}
                    {isActive && (
                      <motion.span
                        layoutId="nav-dot"
                        className="absolute -bottom-2 left-1/2 h-1 w-1 -translate-x-1/2 rounded-full bg-brand-orange"
                      />
                    )}
                  </>
                )}
              </NavLink>
            ))}
            <Link
              to={DIAG_URL}
              data-testid="header-cta-diagnostico"
              className="group flex items-center gap-2 border border-brand-orange/60 px-5 py-2.5 font-mono text-[11px] uppercase tracking-[0.22em] text-brand-orange transition-all duration-300 hover:bg-brand-orange hover:text-white"
            >
              Diagnóstico 4D
              <ArrowUpRight size={14} className="transition-transform duration-300 group-hover:translate-x-0.5 group-hover:-translate-y-0.5" />
            </Link>
          </nav>
          <button
            data-testid="mobile-menu-button"
            onClick={() => setOpen(!open)}
            className="text-white lg:hidden"
            aria-label="Abrir menu"
          >
            {open ? <X size={26} /> : <Menu size={26} />}
          </button>
        </div>
      </header>

      <AnimatePresence>
        {open && (
          <motion.div
            data-testid="mobile-menu"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            transition={{ duration: 0.35 }}
            className="fixed inset-0 z-40 flex flex-col justify-center bg-ink/97 px-8 backdrop-blur-2xl lg:hidden"
          >
            <nav className="flex flex-col gap-2">
              {NAV_LINKS.map((l, i) => (
                <motion.div
                  key={l.to}
                  initial={{ opacity: 0, x: -28 }}
                  animate={{ opacity: 1, x: 0 }}
                  transition={{ delay: 0.08 * i + 0.1, duration: 0.5, ease: [0.16, 1, 0.3, 1] }}
                >
                  <NavLink
                    to={l.to}
                    data-testid={`mobile-${l.testid}`}
                    className={({ isActive }) =>
                      `flex items-center gap-4 border-b border-white/10 py-5 font-display text-4xl font-bold tracking-tight ${
                        isActive ? "text-brand-orange" : "text-white"
                      }`
                    }
                  >
                    <span className="font-mono text-xs text-slate-500">0{i + 1}</span>
                    {l.label}
                  </NavLink>
                </motion.div>
              ))}
            </nav>
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.45, duration: 0.5 }}
            >
              <Link
                to={DIAG_URL}
                data-testid="mobile-cta-diagnostico"
                className="mt-10 flex items-center justify-center gap-2 bg-brand-orange px-6 py-4 font-mono text-xs uppercase tracking-[0.25em] text-white"
              >
                Fazer o diagnóstico 4D <ArrowUpRight size={16} />
              </Link>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>

      <CookieBanner />
    </>
  );
};

// Banner LGPD para o Google Analytics (Consent Mode v2 — default denied no index.html)
const CONSENT_KEY = "fuchs-analytics-consent";

const CookieBanner = () => {
  const [visible, setVisible] = useState(false);

  useEffect(() => {
    const saved = localStorage.getItem(CONSENT_KEY);
    if (saved === "granted") updateAnalyticsConsent(true);
    else if (saved === "denied") updateAnalyticsConsent(false);
    else setVisible(true);
  }, []);

  const choose = (granted) => {
    localStorage.setItem(CONSENT_KEY, granted ? "granted" : "denied");
    updateAnalyticsConsent(granted);
    setVisible(false);
  };

  if (!visible) return null;

  return (
    <motion.div
      data-testid="cookie-banner"
      initial={{ opacity: 0, y: 24 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.4, ease: [0.16, 1, 0.3, 1] }}
      className="fixed inset-x-4 bottom-4 z-[60] mx-auto max-w-2xl border border-white/15 bg-ink/95 p-5 shadow-2xl backdrop-blur-xl sm:p-6"
    >
      <p className="text-sm leading-relaxed text-slate-300">
        Usamos cookies de análise (Google Analytics) para entender como o site é usado e melhorar o conteúdo.
        Nada disso identifica você pessoalmente.
      </p>
      <div className="mt-4 flex flex-col gap-3 sm:flex-row">
        <button
          onClick={() => choose(true)}
          data-testid="cookie-accept"
          className="bg-brand-orange px-6 py-3 font-mono text-[10px] uppercase tracking-[0.2em] text-white transition-colors hover:bg-brand-red"
        >
          Aceitar
        </button>
        <button
          onClick={() => choose(false)}
          data-testid="cookie-reject"
          className="border border-white/20 px-6 py-3 font-mono text-[10px] uppercase tracking-[0.2em] text-slate-300 transition-colors hover:border-brand-orange hover:text-brand-orange"
        >
          Rejeitar
        </button>
      </div>
    </motion.div>
  );
};

export const EditorialMarquee = ({ dark = true }) => (
  <div
    data-testid="editorial-marquee"
    className={`overflow-hidden border-y py-6 ${
      dark ? "border-white/10 bg-navy/60" : "border-navy/10 bg-white"
    }`}
  >
    <Marquee speed={28} gradient={false} pauseOnHover>
      {["Diagnóstico", "Direção", "Desenho", "Desdobramento"].map((w, i) => (
        <span key={i} className="mx-8 flex items-center gap-8">
          <span
            className={`font-display text-3xl font-bold uppercase tracking-[0.15em] md:text-4xl ${
              dark ? "text-outline" : "text-navy/15"
            }`}
          >
            {w}
          </span>
          <span className={`h-2 w-2 rounded-full ${i % 2 ? "bg-brand-magenta" : "bg-brand-orange"}`} />
        </span>
      ))}
    </Marquee>
  </div>
);

const IG_TILES = [
  { src: "/assets/ig-post-1.webp", alt: "Post FUCHS: rejeitamos o modelo tradicional de agência" },
  { src: "/assets/ig-post-2.webp", alt: "Post FUCHS: sua empresa mudou, sua marca mudou com ela?" },
  { src: "/assets/ig-post-3.webp", alt: "Post FUCHS: 15 anos ajudando empresas a decidirem" },
  { src: "/assets/ig-post-4.webp", alt: "Post FUCHS: manifesto — recusamos a comoditização" },
];

const Footer = () => (
  <footer data-testid="site-footer" className="relative overflow-hidden border-t border-white/10 bg-ink">
    <div className="mx-auto max-w-7xl px-6 py-16 lg:px-10 lg:py-20">
      <div className="grid gap-12 md:grid-cols-12">
        <div className="md:col-span-5">
          <Logo />
          <p className="mt-5 max-w-sm font-serif text-lg italic leading-relaxed text-slate-400">
            Posicionamento e direção de marcas. Método, plataforma digital e inteligência trabalhando juntos.
          </p>
          <div className="mt-8">
            <div className="grid max-w-sm grid-cols-4 gap-2" data-testid="footer-instagram-grid">
              {IG_TILES.map((tile, i) => (
                <a
                  key={i}
                  href={INSTA_URL}
                  target="_blank"
                  rel="noopener noreferrer"
                  data-testid={`footer-ig-tile-${i}`}
                  title="Ver no Instagram"
                  className="group relative aspect-[4/5] overflow-hidden border border-white/10"
                >
                  <img
                    src={tile.src}
                    alt={tile.alt}
                    loading="lazy"
                    className="h-full w-full object-cover transition-transform duration-500 group-hover:scale-105"
                  />
                </a>
              ))}
            </div>
            <a
              href={INSTA_URL}
              target="_blank"
              rel="noopener noreferrer"
              data-testid="footer-instagram-follow"
              className="mt-4 inline-flex items-center gap-2 border border-brand-orange/60 px-5 py-2.5 font-mono text-[10px] uppercase tracking-[0.2em] text-brand-orange transition-colors duration-300 hover:bg-brand-orange hover:text-white"
            >
              <Instagram size={13} /> Seguir @fuchs.brand_
            </a>
          </div>
        </div>
        <div className="md:col-span-3">
          <p className="font-mono text-[11px] uppercase tracking-[0.3em] text-brand-orange">Navegação</p>
          <ul className="mt-5 space-y-3">
            {NAV_LINKS.map((l) => (
              <li key={l.to}>
                <Link
                  to={l.to}
                  data-testid={`footer-${l.testid}`}
                  className="text-sm text-slate-400 transition-colors hover:text-white"
                >
                  {l.label}
                </Link>
              </li>
            ))}
            <li>
              <Link
                to="/plataforma-4d"
                data-testid="footer-link-plataforma"
                className="text-sm text-brand-orange transition-colors hover:text-white"
              >
                Plataforma Completa
              </Link>
            </li>
            <li>
              <Link
                to="/agendar?tipo=venda"
                data-testid="footer-link-agendar"
                className="text-sm text-slate-400 transition-colors hover:text-white"
              >
                Agendar reunião
              </Link>
            </li>
          </ul>
        </div>
        <div className="md:col-span-4">
          <p className="font-mono text-[11px] uppercase tracking-[0.3em] text-brand-orange">Canais diretos</p>
          <ul className="mt-5 space-y-3 text-sm">
            <li>
              <a data-testid="footer-email-link" href={EMAIL_URL} className="text-slate-400 transition-colors hover:text-white">
                atendimento@fuchsbrand.com.br
              </a>
            </li>
            <li>
              <a data-testid="footer-whatsapp-link" href={WPP_URL} target="_blank" rel="noopener noreferrer" className="text-slate-400 transition-colors hover:text-white">
                WhatsApp — 47 99253-6961
              </a>
            </li>
            <li>
              <a data-testid="footer-instagram-link" href={INSTA_URL} target="_blank" rel="noopener noreferrer" className="text-slate-400 transition-colors hover:text-white">
                Instagram — @fuchs.brand_
              </a>
            </li>
            <li>
              <a data-testid="footer-linkedin-link" href={LINKEDIN_URL} target="_blank" rel="noopener noreferrer" className="text-slate-400 transition-colors hover:text-white">
                LinkedIn — /in/danyfuchs4D
              </a>
            </li>
          </ul>
        </div>
      </div>
      <div className="mt-16 flex flex-col items-start justify-between gap-4 border-t border-white/10 pt-8 font-mono text-[11px] uppercase tracking-[0.2em] text-slate-600 md:flex-row md:items-center">
        <span>FUCHS — Posicionamento e Direção de Marcas</span>
        <span className="flex items-center gap-6">
          <span>Método 4D · D1 D2 D3 D4</span>
          <Link to="/admin" data-testid="footer-admin-link" className="transition-colors hover:text-brand-orange">
            Área de Gerenciamento
          </Link>
          <Link to="/portal" data-testid="footer-portal-link" className="transition-colors hover:text-brand-orange">
            Área do Cliente
          </Link>
        </span>
      </div>
    </div>
    <div aria-hidden className="pointer-events-none select-none text-center font-display text-[22vw] font-extrabold leading-[0.72] tracking-tight text-white/[0.025]">
      FUCHS
    </div>
  </footer>
);

export default function Layout({ children }) {
  return (
    <div className="min-h-screen bg-ink">
      <Header />
      {children}
      <Footer />
    </div>
  );
}
